'use client'

import { useEffect, useRef, useState } from 'react'
import type { ChatPhase, ChatTranscriptEntry } from '@/hooks/use-postgraduate-chat'
import { CHAT_FLOW, type ChatAnswers, type ChatStepId } from '@/lib/postgraduate-chat/chat-flow'
import { Bubble } from './bubble'
import { ChatOptions } from './chat-options'

interface ChatTranscriptProps {
  readonly transcript: readonly ChatTranscriptEntry[]
  readonly answers: ChatAnswers
  readonly phase: ChatPhase
  readonly onSelectOption: (stepId: ChatStepId, value: string) => void
  readonly onInteractionReadyChange?: (ready: boolean) => void
}

const TYPING_DELAY_MS = 550

/**
 * Revela mensagens do atendente uma de cada vez, com um breve indicador
 * "digitando" antes de cada uma — versão simplificada da animação do
 * chat da Atalia (sem `motion`, mesma ideia).
 */
function useAgentTypingReveal(transcript: readonly ChatTranscriptEntry[]) {
  const [revealedCount, setRevealedCount] = useState(() => transcript.length)
  const [typing, setTyping] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (revealedCount >= transcript.length) {
      setTyping(false)
      return
    }

    const nextEntry = transcript[revealedCount]
    if (nextEntry.kind !== 'agent') {
      setRevealedCount(count => count + 1)
      return
    }

    setTyping(true)
    timeoutRef.current = setTimeout(() => {
      setTyping(false)
      setRevealedCount(count => count + 1)
    }, TYPING_DELAY_MS)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript.length, revealedCount])

  return { revealedCount, typing, interactionReady: !typing && revealedCount >= transcript.length }
}

function TypingDotsBubble() {
  return (
    <Bubble aria-label="Atendente está digitando" className="flex min-h-9 items-center gap-1 px-3.5">
      <span className="size-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.3s]" />
      <span className="size-1.5 animate-bounce rounded-full bg-neutral-400 [animation-delay:-0.15s]" />
      <span className="size-1.5 animate-bounce rounded-full bg-neutral-400" />
    </Bubble>
  )
}

export function ChatTranscript({
  transcript,
  answers,
  phase,
  onSelectOption,
  onInteractionReadyChange,
}: ChatTranscriptProps) {
  const { revealedCount, typing, interactionReady } = useAgentTypingReveal(transcript)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    onInteractionReadyChange?.(interactionReady)
  }, [interactionReady, onInteractionReadyChange])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [])

  const visible = transcript.slice(0, revealedCount)
  const optionsDisabled = phase !== 'perguntando' || !interactionReady

  return (
    <div
      aria-label="Histórico da conversa"
      className="flex h-full flex-col gap-2 overflow-y-auto px-4 py-4"
      role="log"
    >
      {visible.map((entry, index) => {
        const step = entry.stepId ? CHAT_FLOW.find(s => s.id === entry.stepId) : undefined
        const isOptionsStep = step?.answerType === 'options'
        const isLastEntryForStep =
          !isOptionsStep ||
          visible.slice(index + 1).every(later => later.stepId !== entry.stepId)
        const showOptionsHere =
          isOptionsStep &&
          entry.variant === 'question' &&
          step?.options &&
          isLastEntryForStep &&
          revealedCount > index

        return (
          <div className="flex flex-col gap-2" key={entry.id}>
            <Bubble variant={entry.kind === 'user' ? 'user' : entry.variant === 'error' ? 'error' : 'agent'}>
              {entry.text}
            </Bubble>
            {showOptionsHere && step?.options && (
              <ChatOptions
                disabled={optionsDisabled}
                onSelect={value => onSelectOption(step.id, value)}
                options={step.options}
                selected={answers[step.id]}
              />
            )}
          </div>
        )
      })}

      {typing && <TypingDotsBubble />}

      {phase === 'enviando' && !typing && (
        <p className="text-center font-rethink text-neutral-400 text-xs">Enviando…</p>
      )}

      <div ref={bottomRef} />
    </div>
  )
}
